import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Mail, Lock, ArrowLeft, ChevronRight, Loader2, Eye, EyeOff, Check, AlertCircle, Wallet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppKit, useAppKitAccount } from '@reown/appkit/react';
import { useSignMessage, useDisconnect } from 'wagmi';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

async function getFingerprint() {
  try {
    const FpJS = await import('@fingerprintjs/fingerprintjs');
    const fp = await FpJS.default.load();
    const { visitorId } = await fp.get();
    return visitorId;
  } catch { return null; }
}

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: '12px',
  border: '1.5px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#ffffff',
  fontSize: '14px', outline: 'none', transition: 'border-color 0.15s',
  boxSizing: 'border-box',
};

function OptionBtn({ icon, label, onClick, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
        padding: '13px 16px', borderRadius: '14px', border: '1.5px solid rgba(255,255,255,0.15)',
        background: hov ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)', color: '#ffffff', fontSize: '14px',
        fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s', opacity: disabled ? 0.5 : 1,
      }}>
      {icon}
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
      <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.4)' }} />
    </button>
  );
}

function GradientBtn({ children, type = 'button', onClick, disabled }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{
        width: '100%', padding: '13px 16px', borderRadius: '14px', border: 'none',
        background: disabled ? '#d1d5db' : 'linear-gradient(90deg, #22d3ee 0%, #818cf8 50%, #a855f7 100%)',
        color: '#fff', fontSize: '14px', fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        transition: 'opacity 0.15s, transform 0.1s',
        boxShadow: disabled ? 'none' : '0 4px 20px rgba(99,102,241,0.3)',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = '0.92'; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = 'scale(0.985)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >{children}</button>
  );
}

function Divider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0' }}>
      <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
      <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>or</span>
      <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
    </div>
  );
}

export default function LoginModal({ isOpen, onClose, redirectPath = '/' }) {
  const { login, isAuthenticated } = useAuth();
  const [view, setView] = useState('main');
  const [signEmail, setSignEmail] = useState('');
  const [signPassword, setSignPassword] = useState('');
  const [showSignPwd, setShowSignPwd] = useState(false);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fpRef = useRef(null);

  const { open } = useAppKit();
  const { address, isConnected } = useAppKitAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();

  const handleWalletLogin = async () => {
    try {
      if (!isConnected) {
        await open();
        return;
      }
      setLoading(true);
      setError('');
      
      const nonceRes = await fetch(`${API_BASE}/api/auth/wallet/nonce`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: address }),
      });
      const nonceData = await nonceRes.json();
      if (!nonceRes.ok) throw new Error(nonceData.error);
      
      const signature = await signMessageAsync({ message: nonceData.message });
      
      const verifyRes = await fetch(`${API_BASE}/api/auth/wallet/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: address, signature }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error);
      
      localStorage.setItem('daggpt_token', verifyData.token);
      localStorage.setItem('daggpt_user', JSON.stringify(verifyData.user));
      
      const hasEmail = verifyData.user && verifyData.user.email;
      window.location.href = hasEmail ? (redirectPath || '/workspace') : '/dashboard?section=profile';
    } catch (err) {
      if (err.message && err.message.includes('User rejected')) {
        setError('Signature rejected.');
      } else {
        setError(err.message || 'Wallet login failed');
      }
      disconnect();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && isConnected && address && view === 'main' && !loading) {
       handleWalletLogin();
    }
  }, [isConnected, address, isOpen, view]);

  useEffect(() => {
    if (isOpen) { setView('main'); setError(''); getFingerprint().then(fp => { fpRef.current = fp; }); }
  }, [isOpen]);

  useEffect(() => { setError(''); }, [view]);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      if (redirectPath && redirectPath !== window.location.pathname) {
         window.location.href = redirectPath;
      }
      onClose();
    }
  }, [isOpen, isAuthenticated, onClose, redirectPath]);

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE}/api/auth/google?next=${encodeURIComponent(redirectPath)}`;
  };

  const handleSignin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    const result = await login(signEmail, signPassword);
    setLoading(false);
    if (!result?.success) {
      setError(result?.error || 'Invalid email or password');
    } else {
      window.location.href = redirectPath || '/workspace';
      onClose();
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError('');
    if (regPassword !== regConfirm) { setError('Passwords do not match'); return; }
    if (regPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: regEmail, password: regPassword, full_name: regName, fingerprint_id: fpRef.current }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Registration failed');
      } else {
        localStorage.setItem('daggpt_token', data.token);
        localStorage.setItem('daggpt_user', JSON.stringify(data.user));
        
        window.location.href = redirectPath || '/workspace';
      }
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setView('forgot_sent');
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      {/* Backdrop */}
      <motion.div onClick={onClose}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
      />

      {/* Card - exact DAGARMY light style */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.97 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'relative', width: '100%', maxWidth: '420px',
          borderRadius: '20px', background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
          border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}
      >
        {/* Close */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '14px', right: '14px', zIndex: 10,
          width: '30px', height: '30px', borderRadius: '8px', border: 'none',
          background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = '#ffffff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <div style={{ padding: '28px 28px 24px' }}>
          <AnimatePresence mode="wait">

            {/* ── MAIN VIEW ── */}
            {view === 'main' && (
              <motion.div key="main" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                    <img src="/favicon.ico" alt="DAGGPT" style={{ width: '24px', height: '24px' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>Welcome to DAGGPT</div>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>Sign in or create your account</div>
                  </div>
                </div>

                {error && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px', color: '#dc2626' }}>
                    <AlertCircle size={15} style={{ marginTop: '1px', flexShrink: 0 }} />{error}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <OptionBtn icon={<Wallet size={20} />} label={loading && isConnected ? "Signing..." : "Continue with Wallet"} onClick={handleWalletLogin} disabled={loading} />
                  <Divider />
                  <OptionBtn icon={<GoogleIcon />} label="Continue with Google" onClick={handleGoogleLogin} disabled={loading} />
                  <Divider />
                  <OptionBtn icon={<Mail size={20} />} label="Continue with Email" onClick={() => setView('signin')} disabled={loading} />
                  <Divider />
                  <GradientBtn onClick={() => setView('register')} disabled={loading}>
                    Create Account
                  </GradientBtn>
                </div>

                <p style={{ marginTop: '16px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', lineHeight: 1.5 }}>
                  By continuing, you agree to our Terms of Service and Privacy Policy.
                </p>
              </motion.div>
            )}

            {/* ── SIGN IN VIEW ── */}
            {view === 'signin' && (
              <motion.div key="signin" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.18 }}>
                <button onClick={() => setView('main')} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.6)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '18px', padding: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ffffff'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>
                  <ArrowLeft size={15} /> Back
                </button>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '4px' }}>Sign In</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>Enter your email and password to continue.</div>

                {error && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px', color: '#dc2626' }}>
                    <AlertCircle size={15} style={{ marginTop: '1px', flexShrink: 0 }} />{error}
                  </div>
                )}

                <form onSubmit={handleSignin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Email Address</label>
                    <input type="email" value={signEmail} onChange={e => setSignEmail(e.target.value)} required autoFocus placeholder="you@example.com" style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'none'; }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Password</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showSignPwd ? 'text' : 'password'} value={signPassword} onChange={e => setSignPassword(e.target.value)} required placeholder="••••••••"
                        style={{ ...inputStyle, paddingRight: '40px' }}
                        onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                        onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'none'; }} />
                      <button type="button" onClick={() => setShowSignPwd(!showSignPwd)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 0 }}>
                        {showSignPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <button type="button" onClick={() => setView('forgot')} style={{ fontSize: '12px', color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', float: 'right', marginTop: '4px', padding: 0 }}>
                      Forgot password?
                    </button>
                  </div>
                  <GradientBtn type="submit" disabled={loading}>
                    {loading ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Signing in...</> : 'Sign In'}
                  </GradientBtn>
                </form>

                <p style={{ marginTop: '16px', fontSize: '13px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                  Don't have an account?{' '}
                  <button onClick={() => setView('register')} style={{ color: '#6366f1', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Create one</button>
                </p>
              </motion.div>
            )}

            {/* ── REGISTER VIEW ── */}
            {view === 'register' && (
              <motion.div key="register" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.18 }}>
                <button onClick={() => setView('main')} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.6)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '18px', padding: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ffffff'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>
                  <ArrowLeft size={15} /> Back
                </button>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '4px' }}>Create Account</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '18px' }}>Join DAGGPT and access the AI suite.</div>

                {error && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px', color: '#dc2626' }}>
                    <AlertCircle size={15} style={{ marginTop: '1px', flexShrink: 0 }} />{error}
                  </div>
                )}

                <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Full Name</label>
                    <input type="text" value={regName} onChange={e => setRegName(e.target.value)} required autoFocus placeholder="John Doe" style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'none'; }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Email Address</label>
                    <input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} required placeholder="you@example.com" style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'none'; }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Password</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showRegPwd ? 'text' : 'password'} value={regPassword} onChange={e => setRegPassword(e.target.value)} required placeholder="Min 8 chars"
                          style={{ ...inputStyle, paddingRight: '36px' }}
                          onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                          onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'none'; }} />
                        <button type="button" onClick={() => setShowRegPwd(!showRegPwd)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 0 }}>
                          {showRegPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Confirm</label>
                      <input type="password" value={regConfirm} onChange={e => setRegConfirm(e.target.value)} required placeholder="Repeat"
                        style={{ ...inputStyle, borderColor: regConfirm && regConfirm !== regPassword ? '#ef4444' : 'rgba(255,255,255,0.15)' }}
                        onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                        onBlur={e => { e.target.style.borderColor = regConfirm && regConfirm !== regPassword ? '#ef4444' : '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                    </div>
                  </div>
                  <GradientBtn type="submit" disabled={loading}>
                    {loading ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Creating...</> : 'Create Account'}
                  </GradientBtn>
                </form>
                <p style={{ marginTop: '14px', fontSize: '13px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                  Already have an account?{' '}
                  <button onClick={() => setView('signin')} style={{ color: '#6366f1', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Sign in</button>
                </p>
              </motion.div>
            )}

            {/* ── FORGOT VIEW ── */}
            {view === 'forgot' && (
              <motion.div key="forgot" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.18 }}>
                <button onClick={() => setView('signin')} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.6)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '18px', padding: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ffffff'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>
                  <ArrowLeft size={15} /> Back
                </button>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '4px' }}>Reset Password</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>Enter your email and we'll send a reset link.</div>
                {error && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px', color: '#dc2626' }}>
                    <AlertCircle size={15} style={{ marginTop: '1px', flexShrink: 0 }} />{error}
                  </div>
                )}
                <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Email Address</label>
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required autoFocus placeholder="you@example.com" style={inputStyle}
                      onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.boxShadow = 'none'; }} />
                  </div>
                  <GradientBtn type="submit" disabled={loading}>
                    {loading ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Sending...</> : 'Send Reset Link'}
                  </GradientBtn>
                </form>
              </motion.div>
            )}

            {/* ── FORGOT SENT ── */}
            {view === 'forgot_sent' && (
              <motion.div key="forgot_sent" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.22 }} style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <Check size={28} color="#16a34a" />
                </div>
                <div style={{ fontSize: '17px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>Check your inbox</div>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '24px' }}>
                  If <strong style={{ color: '#ffffff' }}>{forgotEmail}</strong> is registered, a reset link is on its way.
                </p>
                <button onClick={() => { setView('signin'); setForgotEmail(''); }} style={{ fontSize: '13px', fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>
                  ← Back to Sign In
                </button>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
